import type {
  PaymentCardFormatterFactory,
  PaymentSdkClient,
  PaymentSdkReadyResponse,
} from '../../application/interfaces/PaymentSdkClient';
import type { PaymentGateway, PayOptions, PaymentResult } from '../../domain/models/payment.types';
import {
  loadRazorpaySdk,
  type RazorpayCreatePaymentPayload,
} from '../sdk/razorpaySdk';

const getErrorMessage = (error: unknown, fallback: string): string => {
  return error instanceof Error && error.message ? error.message : fallback;
};

const buildRazorpayPayload = (options: PayOptions): RazorpayCreatePaymentPayload => {
  const payload: RazorpayCreatePaymentPayload = {
    amount: options.amount,
    currency: options.currency,
    order_id: options.orderId,
    email: options.email,
    contact: options.contact,
    callback_url: options.callbackUrl,
  };

  if (options.customerId) {
    payload.customer_id = options.customerId;
  }

  if (options.cardNumber) {
    payload.method = 'card';
    if (options.offerId) {
      payload.offer_id = options.offerId;
    }
    else{
      payload.recurring = 0;
    }
    payload['card[name]'] = options.name || 'Cardholder';
    payload['card[number]'] = options.cardNumber;
    payload['card[cvv]'] = options.cvv;
    payload['card[expiry_month]'] = options.expiryMonth;
    payload['card[expiry_year]'] = options.expiryYear;
    

    if (options.savePaymentMethod) {
      payload.save = 1;
    }

    return payload;
  }

  if (options.bankCode) {
    payload.method = 'netbanking';
    payload.bank = options.bankCode;
    return payload;
  }

  payload.method = 'upi';

  if (options.useQr) {
    payload.upi = {
      qr: true,
      timeout: 10,
    };
  }

  return payload;
};

const getUpiApp = (options: PayOptions): string | undefined => {
  return options.upiApp || undefined;
};

export class RazorpayAdapter implements PaymentGateway, PaymentSdkClient {
  async load(): Promise<void> {
    await loadRazorpaySdk();
  }

  async onReady(
    key: string,
    handler: (response: PaymentSdkReadyResponse) => void
  ): Promise<void> {
    const RazorpayClass = await loadRazorpaySdk();
    const razorpay = new RazorpayClass({ key });

    if (typeof razorpay.once !== 'function') return;

    razorpay.once('ready', handler);
  }

  getCardFormatter(): PaymentCardFormatterFactory | undefined {
    if (typeof window === 'undefined') return undefined;

    return window.Razorpay?.setFormatter;
  }

  async pay(options: PayOptions): Promise<PaymentResult> {
    const RazorpayClass = await loadRazorpaySdk().catch(() => null);

    if (!RazorpayClass) {
      return {
        success: false,
        error: 'Razorpay SDK is not loaded.',
      };
    }

    try {
      const payload = buildRazorpayPayload(options);
      const app = getUpiApp(options);
      const shouldUseUpiIntent = Boolean(app || options.upiIntent);
      const razorpay = new RazorpayClass({
        key: options.key,
        redirect: true,
        ...(shouldUseUpiIntent ? { webview_intent: true } : {}),
      });

      if (typeof razorpay.createPayment !== 'function') {
        throw new Error('Razorpay direct payment API is not available.');
      }

      if (app) {
        razorpay.createPayment(payload, { app });
      } else {
        razorpay.createPayment(payload);
      }

      return {
        success: true,
        payload,
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error, 'Failed to initialize Razorpay.'),
      };
    }
  }
}

export default RazorpayAdapter;
